import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './Investors.css';

function Investors() {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0);
    setIsLoaded(true);
  }, []);

  return (
    <div className={`investors-page ${isLoaded ? 'loaded' : ''}`}>
      {/* Subtle Background */}
      <div className="background">
        <div className="gradient-wash"></div>
        <div className="vignette"></div>
      </div>

      {/* Navigation */}
      <nav className="nav">
        <Link to="/" className="nav-logo">
          <span className="nav-gradient">Gradient</span> Hill
        </Link>
      </nav>

      {/* Main Content */}
      <main className="investors-content">
        
        {/* Hero Section */}
        <header className="investors-hero">
          <div className="hero-label">For Investors</div>
          <h1 className="hero-headline">
            The World's First<br />
            <span className="gold">AI Agentic Fund</span>
          </h1>
          <p className="hero-subhead">
            Proprietary artificial intelligence that covers every public company on Earth 
            with institutional-grade fundamental research.
          </p>
        </header>

        {/* The Opportunity */}
        <section className="section">
          <div className="section-header">
            <span className="section-number">01</span>
            <h2 className="section-title">The Opportunity</h2>
          </div>
          <div className="section-content">
            <p className="lead-text">
              Traditional fundamental analysis cannot scale. The best analysts can deeply 
              cover 20-30 companies. We cover <em>every company on Earth</em> with the same 
              rigor—simultaneously, continuously, and without bias.
            </p>
            <div className="stats-row">
              <div className="stat">
                <span className="stat-number">50,000+</span>
                <span className="stat-label">Public Companies Analyzed</span>
              </div>
              <div className="stat">
                <span className="stat-number">24/7</span>
                <span className="stat-label">Continuous Monitoring</span>
              </div>
              <div className="stat">
                <span className="stat-number">192</span>
                <span className="stat-label">Dimensions per Company</span>
              </div>
            </div>
          </div>
        </section>

        {/* Divider */}
        <div className="section-divider">
          <span className="divider-diamond">◇</span>
        </div>

        {/* Our Approach */}
        <section className="section">
          <div className="section-header">
            <span className="section-number">02</span>
            <h2 className="section-title">Comprehensive Fundamental Research</h2>
          </div>
          <div className="section-content">
            <p className="lead-text">
              Our AI agents perform deep fundamental analysis across six critical dimensions 
              for every company in our coverage universe.
            </p>
            <div className="pillars-grid">
              <article className="pillar">
                <div className="pillar-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                    <path d="M3 3v18h18" />
                    <path d="M7 16l4-4 4 4 5-6" />
                  </svg>
                </div>
                <h3 className="pillar-title">Markets</h3>
                <p className="pillar-desc">
                  Total addressable market, market dynamics, secular trends, and industry positioning.
                </p>
              </article>
              <article className="pillar">
                <div className="pillar-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                    <path d="M12 2v20M2 12h20" />
                    <path d="M12 6l4 4-4 4-4-4z" />
                  </svg>
                </div>
                <h3 className="pillar-title">Growth</h3>
                <p className="pillar-desc">
                  Revenue trajectory, margin expansion, capital efficiency, and reinvestment opportunities.
                </p>
              </article>
              <article className="pillar">
                <div className="pillar-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                    <circle cx="12" cy="12" r="3" />
                    <circle cx="12" cy="12" r="8" />
                    <path d="M12 2v2M12 20v2M2 12h2M20 12h2" />
                  </svg>
                </div>
                <h3 className="pillar-title">Competitive Dynamics</h3>
                <p className="pillar-desc">
                  Moats, barriers to entry, competitive threats, and sustainable advantages.
                </p>
              </article>
              <article className="pillar">
                <div className="pillar-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <path d="M3 9h18M9 3v18" />
                  </svg>
                </div>
                <h3 className="pillar-title">Valuation</h3>
                <p className="pillar-desc">
                  Intrinsic value, relative metrics, scenario analysis, and margin of safety.
                </p>
              </article>
              <article className="pillar">
                <div className="pillar-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                    <circle cx="12" cy="7" r="4" />
                    <path d="M5 21v-2a7 7 0 0114 0v2" />
                  </svg>
                </div>
                <h3 className="pillar-title">Management</h3>
                <p className="pillar-desc">
                  Leadership quality, capital allocation history, incentive alignment, and track record.
                </p>
              </article>
              <article className="pillar">
                <div className="pillar-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                    <path d="M12 9v4M12 17h.01" />
                    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  </svg>
                </div>
                <h3 className="pillar-title">Risks</h3>
                <p className="pillar-desc">
                  Regulatory exposure, concentration risks, balance sheet vulnerabilities, and tail events.
                </p>
              </article>
            </div>
          </div>
        </section>

        {/* Divider */}
        <div className="section-divider">
          <span className="divider-diamond">◇</span>
        </div>

        {/* Technology */}
        <section className="section">
          <div className="section-header">
            <span className="section-number">03</span>
            <h2 className="section-title">Proprietary Technology</h2>
          </div>
          <div className="section-content">
            <div className="tech-intro">
              <p className="lead-text">
                Our breakthrough technology draws inspiration from the Intelligence Community's 
                rigorous methodologies for information analysis and synthesis.
              </p>
            </div>
            
            <div className="tech-features">
              <article className="tech-feature">
                <div className="feature-marker"></div>
                <div className="feature-content">
                  <h3 className="feature-title">Source Ranking Architecture</h3>
                  <p className="feature-desc">
                    Inspired by intelligence tradecraft, our systems evaluate and rank information 
                    sources by reliability, timeliness, and historical accuracy. This creates a 
                    hierarchy of trust that filters noise from signal at unprecedented scale.
                  </p>
                </div>
              </article>
              
              <article className="tech-feature">
                <div className="feature-marker"></div>
                <div className="feature-content">
                  <h3 className="feature-title">Fact Alignment Engine</h3>
                  <p className="feature-desc">
                    Our proprietary algorithms cross-reference and align facts across thousands 
                    of sources, identifying inconsistencies, confirming data points, and building 
                    a coherent picture of each company's true state.
                  </p>
                </div>
              </article>
              
              <article className="tech-feature">
                <div className="feature-marker"></div>
                <div className="feature-content">
                  <h3 className="feature-title">World-Record AI Reasoning</h3>
                  <p className="feature-desc">
                    Our AI agents achieve world-record performance in valuation reasoning tasks, 
                    synthesizing quantitative data with qualitative insights to reach conclusions 
                    that transcend traditional analytical capabilities.
                  </p>
                </div>
              </article>
            </div>
          </div>
        </section>

        {/* Divider */}
        <div className="section-divider">
          <span className="divider-diamond">◇</span>
        </div>

        {/* Why Now */}
        <section className="section">
          <div className="section-header">
            <span className="section-number">04</span>
            <h2 className="section-title">The Inflection Point</h2>
          </div>
          <div className="section-content">
            <blockquote className="highlight-quote">
              "For the first time in history, artificial intelligence can reason about 
              valuation with depth and nuance that exceeds human capability—not by replacing 
              human judgment, but by extending it to a scale previously unimaginable."
            </blockquote>
            <div className="why-now-grid">
              <div className="why-item">
                <h4>AI Capability Threshold</h4>
                <p>
                  Recent advances in large language models and reasoning architectures have 
                  crossed the threshold required for genuine fundamental analysis.
                </p>
              </div>
              <div className="why-item">
                <h4>Data Abundance</h4>
                <p>
                  The explosion of structured and unstructured data creates opportunities 
                  for those who can process it effectively.
                </p>
              </div>
              <div className="why-item">
                <h4>First-Mover Advantage</h4>
                <p>
                  Early movers in AI-native investing will compound advantages through 
                  proprietary data, refined models, and institutional learning.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Divider */}
        <div className="section-divider">
          <span className="divider-diamond">◇</span>
        </div>

        {/* Team */}
        <section className="section team-section">
          <div className="section-header">
            <span className="section-number">05</span>
            <h2 className="section-title">Leadership</h2>
          </div>
          <div className="section-content">
            <p className="lead-text centered">
              Gradient Hill combines world-class expertise in artificial intelligence 
              with decades of investment management experience.
            </p>
            <div className="team-grid-detailed">
              <div className="team-member-detailed">
                <div className="member-header">
                  <div className="member-name-large">Shadi Copty</div>
                  <div className="member-title">Co-Founder & AI Strategy</div>
                </div>
                <div className="member-credentials">
                  <div className="credential-section">
                    <h4>Experience</h4>
                    <ul className="credential-list">
                      <li>Senior Director, Partner Engineering for Llama · <strong>Meta</strong></li>
                      <li>VP Product Management, Watson Data & AI · <strong>IBM</strong></li>
                      <li>Head of Data Science & AI Runtimes, Watson · <strong>IBM</strong></li>
                    </ul>
                  </div>
                  <div className="credential-section">
                    <h4>Education</h4>
                    <p>MBA & BSc Computer Science · <strong>Technion – Israel Institute of Technology</strong></p>
                  </div>
                </div>
                <p className="member-summary">
                  Led multi-billion dollar AI product portfolios. Deep expertise in machine learning, 
                  statistical learning, and building production-grade AI systems at scale.
                </p>
              </div>

              <div className="team-member-detailed">
                <div className="member-header">
                  <div className="member-name-large">Iyad Farah</div>
                  <div className="member-title">Co-Founder & Investment Strategy</div>
                </div>
                <div className="member-credentials">
                  <div className="credential-section">
                    <h4>Experience</h4>
                    <ul className="credential-list">
                      <li>Head of Equity Solutions · <strong>Aviva Investors</strong></li>
                      <li>CEO · <strong>Per4m Asset Management</strong></li>
                      <li>Managed $35B in institutional assets</li>
                    </ul>
                  </div>
                  <div className="credential-section">
                    <h4>Education & Credentials</h4>
                    <p>PhD Applied Mathematics · IIMR (CFA)</p>
                  </div>
                </div>
                <p className="member-summary">
                  Seasoned investment professional with extensive experience across global capital 
                  markets, equity solutions, and institutional portfolio management at scale.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="cta-section">
          <div className="cta-content">
            <h2 className="cta-title">Begin the Conversation</h2>
            <p className="cta-subtitle">
              For qualified investors interested in learning more about Gradient Hill.
            </p>
            <a href="mailto:contact@gradienthill.com" className="cta-button">
              contact@gradienthill.com
            </a>
          </div>
        </section>

        {/* Footer */}
        <footer className="investors-footer">
          <div className="footer-ornament"></div>
          <p className="copyright">© 2025 Gradient Hill</p>
          <p className="disclaimer">
            This material is for informational purposes only and does not constitute an offer 
            to sell or a solicitation of an offer to buy any securities.
          </p>
        </footer>
      </main>
    </div>
  );
}

export default Investors;

